import { TilesError, throwIfTilesAborted } from '@/plugins/tiles/TilesError'

interface Waiter {
  readonly bytes: number
  readonly signal: AbortSignal
  readonly resolve: (release: () => void) => void
  readonly reject: (error: unknown) => void
  abort(): void
}

/** 输出在解码入槽前预约；超过软水位的单项仅在空队列独占推进。 */
export class TilePreparationCapacity {
  private readonly _queue: Waiter[] = []
  private _bytes = 0
  private _active = 0
  private _throttled = false

  constructor(
    readonly highBytes = 128 * 1024 ** 2,
    readonly lowBytes = 64 * 1024 ** 2
  ) {
    if (
      !Number.isSafeInteger(highBytes) ||
      highBytes < 1 ||
      !Number.isSafeInteger(lowBytes) ||
      lowBytes < 0 ||
      lowBytes >= highBytes
    )
      throw new RangeError('准备水位必须满足 0 <= lowBytes < highBytes')
  }

  async acquire(bytes: number, signal: AbortSignal): Promise<() => void> {
    throwIfTilesAborted(signal)
    if (!Number.isSafeInteger(bytes) || bytes < 0)
      throw new RangeError('准备成本必须为非负安全整数')
    if (this._queue.length >= 4096)
      throw new TilesError({
        code: 'budget-exceeded',
        stage: 'runtime',
        message: '准备候选队列容量已满',
      })
    return new Promise((resolve, reject) => {
      const waiter: Waiter = {
        bytes,
        signal,
        resolve,
        reject,
        abort: () => {
          const index = this._queue.indexOf(waiter)
          if (index < 0) return
          this._queue.splice(index, 1)
          signal.removeEventListener('abort', waiter.abort)
          reject(new TilesError({ code: 'cancelled', stage: 'runtime', message: '准备预约已取消' }))
          this.pump()
        },
      }
      this._queue.push(waiter)
      signal.addEventListener('abort', waiter.abort, { once: true })
      this.pump()
    })
  }

  getStatus() {
    return Object.freeze({
      reservedBytes: this._bytes,
      active: this._active,
      queued: this._queue.length,
      queuedBytes: this._queue.reduce((sum, item) => sum + item.bytes, 0),
      throttled: this._throttled,
      exclusive: this._active === 1 && this._bytes > this.highBytes,
      highBytes: this.highBytes,
      lowBytes: this.lowBytes,
    })
  }

  private pump(): void {
    if (this._bytes <= this.lowBytes) this._throttled = false
    while (this._queue.length) {
      const next = this._queue[0]
      if (this._active && (this._throttled || this._bytes + next.bytes > this.highBytes)) {
        this._throttled = true
        return
      }
      this._queue.shift()
      next.signal.removeEventListener('abort', next.abort)
      this._bytes += next.bytes
      this._active++
      let released = false
      next.resolve(() => {
        if (released) return
        released = true
        this._bytes -= next.bytes
        this._active--
        this.pump()
      })
    }
  }
}
